import { PaperWrapped, HeaderWrapped } from '../lab-result/component/styled';
import { ButtonEnhance } from '@/module/common/componentUI/commonStyleComponents';
import {
  fetchSignedUrl,
  keyFromAnyInput,
} from '@/module/download-plato-files/utils';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';

/**
 * Manual counterpart to the "Minc Plato File Bridge" extension: paste a file
 * link, get the file. For Safari, shared machines, a link forwarded by email,
 * or anyone waiting on IT to install the extension.
 *
 * The link is only ever turned into an S3 object key here; the signed URL is
 * issued by the BE against the logged-in user, exactly as on the extension
 * landing page (@/module/download-plato-files).
 */

// Same field styling the service forms use, so the input matches the rest of
// the admin screens.
const CustomTextField = withStyles({
  root: {
    fontSize: 20,

    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      height: 48,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: 0,
      paddingLeft: 5,
      fontSize: 16,
    },
    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
  },
})(TextField);

const CustomTitleLabel = withStyles({
  root: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 4,
    color: '#666666',
  },
})(Typography);

const PLACEHOLDER = 'Enter link';

const INVALID_LINK_MESSAGE =
  'This does not look like a Minc file link. Paste the full link, or the file path on its own.';

const NOT_FOUND_MESSAGE =
  'Could not get a download link for this file. It may have been moved, or you may not have access to it.';

const PlatoFileDownload = () => {
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClear = () => {
    setLink('');
    setError('');
  };

  const handleDownload = async () => {
    const fileName = keyFromAnyInput(link);
    if (!fileName) {
      setError(INVALID_LINK_MESSAGE);
      return;
    }

    setLoading(true);
    setError('');

    const signedUrl = await fetchSignedUrl(fileName);
    setLoading(false);

    if (!signedUrl) {
      setError(NOT_FOUND_MESSAGE);
      return;
    }

    /* Deliberately NOT window.location.assign: that navigates this window, so
       the screen is torn down and the pasted link is lost even though the file
       downloads. A detached anchor hands the URL to the browser while leaving
       the page alone, ready for the next link. */
    const anchor = document.createElement('a');
    anchor.href = signedUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <PaperWrapped>
      <HeaderWrapped className="no-tabs-header">
        <Typography style={{ fontSize: 18, fontWeight: 600 }}>
          Download Plato File
        </Typography>

        <div className="personal-btn">
          <ButtonEnhance
            background="#E9E9E9"
            color="black"
            backgroundHover="#ccc"
            onClick={handleClear}
            disabled={loading}
          >
            Clear
          </ButtonEnhance>
          <ButtonEnhance onClick={handleDownload} disabled={loading || !link}>
            Download
          </ButtonEnhance>
        </div>
      </HeaderWrapped>

      <Box px={2.5} py={3}>
        <Typography style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
          Paste the file link from Plato.
        </Typography>

        <CustomTitleLabel>File link</CustomTitleLabel>
        <CustomTextField
          fullWidth
          variant="outlined"
          placeholder={PLACEHOLDER}
          value={link}
          onChange={(e) => {
            setLink(e.target.value);
            if (error) setError('');
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !loading && link) handleDownload();
          }}
          error={!!error}
          helperText={error}
          disabled={loading}
        />

        {loading && (
          <Box display="flex" alignItems="center" mt={2} style={{ gap: 12 }}>
            <CircularProgress size={20} />
            <Typography style={{ fontSize: 14, color: '#666' }}>
              Preparing your file...
            </Typography>
          </Box>
        )}
      </Box>
    </PaperWrapped>
  );
};

export default PlatoFileDownload;
